import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { Building2, Plus, Trash2, AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { BankIntegrationConfig, BankAccountMapping } from '../../../../types/settings';
import toast from 'react-hot-toast';

const BANK_LABELS: Record<string, string> = {
  trust_bank: 'Trust Merchant Bank (TMB)',
  equity_bank: 'Equity Bank Congo',
  rawbank: 'Rawbank',
  other: 'Autre institution',
};

const FREQ_OPTIONS = [
  { value: 'hourly', label: 'Toutes les heures' },
  { value: 'daily',  label: 'Quotidienne' },
  { value: 'weekly', label: 'Hebdomadaire' },
];

export function BankIntegration() {
  const { isAdmin, can } = usePermissions();
  const canManage = can('manage_integrations') || isAdmin;

  const [banks, setBanks] = useState<BankIntegrationConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await settingsApi.getBankIntegrations();
        if (!res.success || !res.data) throw new Error();
        setBanks(Array.isArray(res.data) ? res.data : [res.data]);
      } catch {
        setError('Impossible de charger les intégrations bancaires.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const save = async (bank: BankIntegrationConfig) => {
    if (!canManage) return;
    setSaving(bank.provider);
    try {
      const { provider, ...payload } = bank;
      const res = await settingsApi.updateBankIntegration(provider, payload);
      if (!res.success) throw new Error();
      setBanks(prev => prev.map(b => b.provider === provider ? { ...b, ...res.data } : b));
      toast.success(`${BANK_LABELS[provider] ?? provider} mis à jour`);
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const updateField = <K extends keyof BankIntegrationConfig>(
    provider: string, key: K, value: BankIntegrationConfig[K]
  ) => {
    setBanks(prev => prev.map(b => b.provider === provider ? { ...b, [key]: value } : b));
  };

  const addMapping = (provider: string) => {
    setBanks(prev => prev.map(b =>
      b.provider === provider
        ? { ...b, accountMappings: [...b.accountMappings, { bankAccountId: '', accountingAccountId: '', description: '' }] }
        : b
    ));
  };

  const removeMapping = (provider: string, idx: number) => {
    setBanks(prev => prev.map(b =>
      b.provider === provider
        ? { ...b, accountMappings: b.accountMappings.filter((_, i) => i !== idx) }
        : b
    ));
  };

  const updateMapping = (provider: string, idx: number, key: keyof BankAccountMapping, value: string) => {
    setBanks(prev => prev.map(b =>
      b.provider === provider
        ? { ...b, accountMappings: b.accountMappings.map((m, i) => i === idx ? { ...m, [key]: value } : m) }
        : b
    ));
  };

  if (isLoading) {
    return (
      <Card title="Intégrations Bancaires" icon={Building2}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Intégrations Bancaires" icon={Building2}>
      <div className="space-y-4">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Synchronisez vos comptes bancaires avec votre plan comptable SYSCOHADA pour importer automatiquement les relevés.
        </p>

        {banks.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">Aucune intégration bancaire configurée.</p>
        )}

        {banks.map(bank => {
          const isExpanded = expanded === bank.provider;
          const isSaving = saving === bank.provider;
          return (
            <div key={bank.provider} className="border border-border rounded-lg overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{BANK_LABELS[bank.provider] ?? bank.provider}</p>
                    {bank.lastSyncDate && (
                      <p className="text-xs text-muted-foreground">
                        Sync: {new Date(bank.lastSyncDate).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={bank.enabled}
                    onCheckedChange={v => updateField(bank.provider, 'enabled', v)}
                    disabled={!canManage || !!saving}
                  />
                  <button
                    onClick={() => setExpanded(isExpanded ? null : bank.provider)}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {/* Expandable config */}
              {isExpanded && (
                <div className="px-4 py-4 space-y-4 border-t border-border">
                  {/* Sync frequency */}
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-foreground">Fréquence de synchronisation</label>
                    <select
                      className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground"
                      value={bank.syncFrequency}
                      onChange={e => updateField(bank.provider, 'syncFrequency', e.target.value as any)}
                      disabled={!canManage}
                    >
                      {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>

                  {/* Account mappings */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-foreground">Correspondances de comptes</p>
                      {canManage && (
                        <Button variant="ghost" size="sm" icon={Plus} onClick={() => addMapping(bank.provider)}>
                          Ajouter
                        </Button>
                      )}
                    </div>
                    {bank.accountMappings.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2">Aucune correspondance configurée.</p>
                    ) : (
                      <div className="space-y-2">
                        {bank.accountMappings.map((m, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center">
                            <input
                              className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                              placeholder="N° bancaire"
                              value={m.bankAccountId}
                              onChange={e => updateMapping(bank.provider, idx, 'bankAccountId', e.target.value)}
                              disabled={!canManage}
                            />
                            <input
                              className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                              placeholder="Compte (ex: 521100)"
                              value={m.accountingAccountId}
                              onChange={e => updateMapping(bank.provider, idx, 'accountingAccountId', e.target.value)}
                              disabled={!canManage}
                            />
                            <input
                              className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                              placeholder="Description"
                              value={m.description}
                              onChange={e => updateMapping(bank.provider, idx, 'description', e.target.value)}
                              disabled={!canManage}
                            />
                            {canManage && (
                              <button onClick={() => removeMapping(bank.provider, idx)} className="text-destructive hover:text-destructive/70">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex justify-end pt-2 border-t border-border">
                      <Button
                        size="sm"
                        onClick={() => save(bank)}
                        disabled={!!saving}
                      >
                        {isSaving ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Enregistrement...</> : 'Enregistrer'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}